import { useMemo, useState } from 'react';
import { useTranslation } from '@frontend/i18n';
import { Tray, useFormField } from '@frontend/design-system';
import { useCalendarViewV3HeaderContext } from '../../../context/CalendarViewV3HeaderContext';
import { useCalendarV3ShallowStore } from '../../../stores/use-calendar-v3-store';
import { CALENDAR_TRACKING_IDS } from '../../../trackingIds/calendar-tracking-ids';
import { hasSameIds } from '../../../utils';
import { DisabledCalendarFilterSection } from '../../../views/Calendar/components/HeaderBar/Filter/DisabledCalendarFilterSection';
import { MultiLocationSelector } from '../../../views/Calendar/components/HeaderBar/Filter/MultiLocationSelector';
import { CalendarViewV3HeaderAppointmentTypeFilter } from './CalendarViewV3HeaderAppointmentTypeFilter';
import { CalendarViewV3HeaderConfirmationStatusFilter } from './CalendarViewV3HeaderConfirmationStatusFilter';
import { CalendarViewV3HeaderPractitionerFilter } from './CalendarViewV3HeaderPractitionerFilter';

const trackingIds = CALENDAR_TRACKING_IDS.headerBarFilter;

type CalendarViewV3HeaderFilterModalContentProps = {
  closeModal: () => void;
};

export const CalendarViewV3HeaderFilterModalContent = ({ closeModal }: CalendarViewV3HeaderFilterModalContentProps) => {
  const { t } = useTranslation('scheduleCalendarFilters');

  const { selectedMultiLocationIds, appointmentStatusList } = useCalendarViewV3HeaderContext();
  const {
    calendarLocationIds,
    calendarProviderIds,
    calendarApptTypeIds,
    calendarApptStatus,
    setCalendarFilters,
    setCalendarLocationIds,
  } = useCalendarV3ShallowStore(
    'calendarLocationIds',
    'calendarProviderIds',
    'calendarApptTypeIds',
    'calendarApptStatus',
    'setCalendarFilters',
    'setCalendarLocationIds'
  );

  const [locationIds, setLocationIds] = useState<string[]>(
    calendarLocationIds.length ? calendarLocationIds : selectedMultiLocationIds
  );

  const hasOnlyOneLocation = selectedMultiLocationIds.length === 1;

  const practitionersCheckListField = useFormField(
    {
      type: 'checklist',
      value: calendarProviderIds,
    },
    [calendarProviderIds]
  );

  const appointmentTypesCheckListField = useFormField(
    {
      type: 'checklist',
      value: calendarApptTypeIds,
    },
    [calendarApptTypeIds]
  );

  const confirmationStatusField = useFormField({ type: 'radio', value: calendarApptStatus }, [calendarApptStatus]);

  const applyFilters = () => {
    setCalendarFilters({
      providerIds: practitionersCheckListField.value,
      apptTypeIds: appointmentTypesCheckListField.value,
      apptStatus: confirmationStatusField.value,
    });
    setCalendarLocationIds(locationIds);
    closeModal();
  };

  const resetFilters = () => {
    setCalendarLocationIds(selectedMultiLocationIds, true);
    closeModal();
  };

  const IsFilterStateChanged = useMemo(() => {
    const isPractitionerFilterChanged = !hasSameIds(calendarProviderIds, practitionersCheckListField.value);

    const isAppointmentTypeFilterChanged = !hasSameIds(calendarApptTypeIds, appointmentTypesCheckListField.value);

    const isLocationFilterChanged = !hasSameIds(
      locationIds,
      calendarLocationIds.length ? calendarLocationIds : selectedMultiLocationIds
    );

    const isConfirmationStatusFilterChanged = calendarApptStatus !== confirmationStatusField.value;

    return (
      isPractitionerFilterChanged ||
      isAppointmentTypeFilterChanged ||
      isLocationFilterChanged ||
      isConfirmationStatusFilterChanged
    );
  }, [
    calendarProviderIds,
    calendarApptTypeIds,
    calendarApptStatus,
    confirmationStatusField.value,
    practitionersCheckListField.value,
    appointmentTypesCheckListField.value,
    locationIds,
    calendarLocationIds,
    selectedMultiLocationIds,
  ]);

  const hasErrorOrMissingLocation = hasOnlyOneLocation ? !!practitionersCheckListField.error : locationIds.length === 0;

  const disablePrimaryActionBtn = !IsFilterStateChanged || hasErrorOrMissingLocation;

  return (
    <>
      {!hasOnlyOneLocation && <MultiLocationSelector locationIds={locationIds} setLocationIds={setLocationIds} />}
      <CalendarViewV3HeaderPractitionerFilter
        checkListProps={practitionersCheckListField}
        hasOnlyOneLocation={hasOnlyOneLocation}
        locationIds={locationIds}
      />
      <CalendarViewV3HeaderAppointmentTypeFilter
        checkListProps={appointmentTypesCheckListField}
        hasOnlyOneLocation={hasOnlyOneLocation}
        locationIds={locationIds}
      />
      <CalendarViewV3HeaderConfirmationStatusFilter
        confirmationStatusField={confirmationStatusField}
        defaultAppointmentStatusList={appointmentStatusList}
      />

      <DisabledCalendarFilterSection label={t('Operatory')} tooltip={t('Operatories coming soon')} />

      <Tray.Actions
        onPrimaryClick={applyFilters}
        disabledPrimary={disablePrimaryActionBtn}
        primaryLabel={t('Apply Filters')}
        onSecondaryClick={resetFilters}
        secondaryLabel={t('Reset')}
        primaryTrackingId={trackingIds.applyFiltersButton}
        secondaryTrackingId={trackingIds.resetFiltersButton}
      />
    </>
  );
};
